import startCase from 'lodash/startCase';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useSelector } from 'react-redux';

import { CountryCodes } from 'src/common/enums';
import { getUtmParams } from 'src/common/links';
import {
  ID_JOB_EXPLORE_URL,
  JOB_EXPLORE_URL,
  VN_JOB_EXPLORE_URL,
} from 'src/common/routes';
import { getBasename, getCountry } from 'src/modules/ClientConfig/Selectors';
import { useCategoryIdFromSlug } from 'src/modules/Opportunities/hooks/useCategoryIdFromSlug';
import { extractSlugFromFullSlug } from 'src/modules/Opportunities/utils/createUrlSlug';
import { getLanguage } from 'src/selectors/locale';

import { useCustomSlugQuery } from '../hooks/useCustomSlugQuery';
import { useLocationIdFromSlug } from '../hooks/useLocationIdFromSlug';

const getSlugUrlParams = (pathname: string, countryCode: CountryCodes) => {
  switch (pathname) {
    case JOB_EXPLORE_URL:
      return {
        query: {
          country: countryCode,
        },
      };
    case ID_JOB_EXPLORE_URL:
      return {
        query: {
          country: 'ID',
        },
      };
    case VN_JOB_EXPLORE_URL:
      return {
        query: {
          country: 'VN',
        },
      };
    case `${ID_JOB_EXPLORE_URL}-design`:
      return {
        slug: 'design',
        query: {
          country: 'ID',
          jobCategories: '3',
        },
      };
    case `${ID_JOB_EXPLORE_URL}-software-engineer`:
      return {
        slug: 'software-engineer',
        query: {
          country: 'ID',
          jobCategories: '1',
        },
      };
    case `${ID_JOB_EXPLORE_URL}-marketing`:
      return {
        slug: 'marketing',
        query: {
          country: 'ID',
          jobCategories: '5',
        },
      };
    case `${ID_JOB_EXPLORE_URL}-media-communication`:
      return {
        slug: 'media-communicate',
        query: {
          country: 'ID',
          jobCategories: '12',
        },
      };
    case `${ID_JOB_EXPLORE_URL}-sales-business-development`:
      return {
        slug: 'sales-business-development',
        query: {
          country: 'ID',
          jobCategories: '7',
        },
      };
    case `${ID_JOB_EXPLORE_URL}-operations`:
      return {
        slug: 'operations',
        query: {
          country: 'ID',
          jobCategories: '4',
        },
      };
    case `${ID_JOB_EXPLORE_URL}-human-resource`:
      return {
        slug: 'human-resource',
        query: {
          country: 'ID',
          jobCategories: '11',
        },
      };
    case `${ID_JOB_EXPLORE_URL}-finance`:
      return {
        slug: 'finance',
        query: {
          country: 'ID',
          jobCategories: '10',
        },
      };
    case `${ID_JOB_EXPLORE_URL}-legal`:
      return {
        slug: 'legal',
        query: {
          country: 'ID',
          jobCategories: '51',
        },
      };
    case `${ID_JOB_EXPLORE_URL}-product-management`:
      return {
        slug: 'product-management',
        query: {
          country: 'ID',
          jobCategories: '6',
        },
      };
    case `${ID_JOB_EXPLORE_URL}-engineering`:
      return {
        slug: 'engineering',
        query: {
          country: 'ID',
          jobCategories: '8',
        },
      };
    case `${ID_JOB_EXPLORE_URL}-data-science`:
      return {
        slug: 'data-science',
        query: {
          country: 'ID',
          jobCategories: '2',
        },
      };
    default:
      return null;
  }
};

type TransformSlugUrlArg = {
  countryCode: CountryCodes;
  language: string;
  basename: string;
  asPath: string;
  query: ParsedUrlQuery;
  customSlugQuery?: ParsedUrlQuery;
  locationId?: string;
  categoryId?: string;
};

type TransformSlugUrlReturn = {
  slug?: string;
  query: ParsedUrlQuery;
  isNotSlugUrl?: boolean;
  type?: SlugTypes;
};

export enum SlugTypes {
  LOCATION = 'location',
  COMPANY = 'company',
  CAREER = 'category',
  QUERY = 'query',
  JOB_ROLE = 'job-role',
  CUSTOM = 'find-jobs',
  JOB_LOCATION = 'job-location',
  JOB_CATEGORY = 'job-category',
  INTERNAL_SEARCH = 'explore',
}

const getSEOPageSlugUrlParams = (
  countryCode: CountryCodes,
  language: string,
  pathname: string,
  customSlugQuery?: ParsedUrlQuery,
  locationId?: string,
  categoryId?: string
) => {
  if (
    !pathname.match(
      /^\/(location|company|category|query|job-role)\/([^/]+)\/([^/]+)/
    ) &&
    !pathname.match(/^\/find-jobs\/([^/]+)/) &&
    !pathname.match(/^\/job-location\/([^/]+)/) &&
    !pathname.match(/^\/job-category\/([^/]+)/)
  ) {
    return null;
  }

  const segments = pathname.split('/').splice(1);
  const type = segments[0];

  // Return early for the new location page because it has different url structure
  if (type === SlugTypes.JOB_LOCATION) {
    return {
      query: { locationId },
      type: SlugTypes.JOB_LOCATION,
    };
  }

  // Return early for the new category page because it has different url structure
  if (type === SlugTypes.JOB_CATEGORY) {
    return {
      query: { categoryId },
      type: SlugTypes.JOB_CATEGORY,
    };
  }

  const slug = startCase(
    extractSlugFromFullSlug(
      decodeURIComponent(segments[1]),
      countryCode,
      language
    )
  );
  const id = type !== SlugTypes.CUSTOM && segments[2].split('?')[0];

  switch (type) {
    case SlugTypes.LOCATION:
      return {
        slug,
        query: { cities: id },
        type: SlugTypes.LOCATION,
      };
    case SlugTypes.COMPANY:
      return {
        slug,
        query: { companies: id },
        type: SlugTypes.COMPANY,
      };
    case SlugTypes.CAREER:
      return {
        slug,
        query: {
          jobCategories: id,
          country: countryCode,
        },
        type: SlugTypes.CAREER,
      };
    case SlugTypes.QUERY:
      return {
        slug,
        query: {
          freeQueryId: id,
          country: countryCode,
        },
        type: SlugTypes.QUERY,
      };
    case SlugTypes.JOB_ROLE:
      return {
        slug,
        query: {
          jobRole: id,
          country: countryCode,
        },
        type: SlugTypes.JOB_ROLE,
      };
    case SlugTypes.CUSTOM:
      return {
        slug,
        query: customSlugQuery,
        type: SlugTypes.CUSTOM,
      };
    default:
      return null;
  }
};

export const transformSlugUrlParams = ({
  countryCode,
  language,
  basename,
  asPath,
  query,
  customSlugQuery,
  locationId,
  categoryId,
}: TransformSlugUrlArg): TransformSlugUrlReturn => {
  // remove basename from asPath if exist
  // eg: /id/lowongan-kerja-jakarta -> /lowongan-kerja-jakarta
  const pathname = basename ? asPath.replace(basename, '') : asPath;
  const slugUrlParams = getSlugUrlParams(pathname.split('?')[0], countryCode);
  const utmParams = getUtmParams(query);

  if (slugUrlParams) {
    return {
      ...slugUrlParams,
      query: {
        ...slugUrlParams.query,
        ...(query.page ? { page: query.page } : {}),
        ...utmParams,
      },
    };
  }

  const seoPageSlugUrlParams = getSEOPageSlugUrlParams(
    countryCode,
    language,
    pathname,
    customSlugQuery,
    locationId,
    categoryId
  );

  if (seoPageSlugUrlParams) {
    return {
      ...seoPageSlugUrlParams,
      query: {
        ...seoPageSlugUrlParams.query,
        ...(query.page ? { page: query.page } : {}),
        ...utmParams,
      },
    };
  }

  return { query, isNotSlugUrl: true };
};

export const useSlugUrlParams = () => {
  const { asPath, query } = useRouter();
  const basename = useSelector(getBasename);
  const countryCode = useSelector(getCountry);
  const language = useSelector(getLanguage);
  const pathname = basename ? asPath.replace(basename, '') : asPath;
  const { filter: customSlugQuery } = useCustomSlugQuery(pathname);
  const locationId = useLocationIdFromSlug(pathname);
  const categoryId = useCategoryIdFromSlug(pathname);

  return transformSlugUrlParams({
    countryCode,
    language,
    basename,
    asPath,
    query,
    customSlugQuery,
    locationId,
    categoryId,
  });
};
